// src/pages/Legal/PrivacyPolicy.tsx

import React from 'react';
import styles from './Legal.module.css';

const TOC = [
  { id: 'info-collect', label: 'Information We Collect' },
  { id: 'info-use', label: 'How We Use Your Information' },
  { id: 'info-sharing', label: 'Information Sharing' },
  { id: 'data-security', label: 'Data Security' },
  { id: 'data-retention', label: 'Data Retention' },
  { id: 'your-rights', label: 'Your Rights' },
  { id: 'cookies', label: 'Cookies' },
  { id: 'children', label: "Children's Privacy" },
  { id: 'changes', label: 'Changes to This Policy' },
  { id: 'contact', label: 'Contact' },
];

const PrivacyPolicy: React.FC = () => {
  return (
    <div className={styles.legalPage} id="top">
      {/* Header */}
      <header className={styles.header}>
        <h1 className={styles.title}>Privacy Policy</h1>
        <p className={styles.lastUpdated}>Last updated: March 15, 2026</p>
      </header>

      {/* Table of Contents */}
      <nav className={styles.toc}>
        <p className={styles.tocTitle}>Table of Contents</p>
        <ol className={styles.tocList}>
          {TOC.map((item) => (
            <li key={item.id}>
              <a href={`#${item.id}`} className={styles.tocLink}>
                {item.label}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <p style={{ fontSize: 15, lineHeight: 1.75, color: 'var(--text-secondary)' }}>
        SignalDeck ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we
        collect, use, and safeguard your information when you use our automated trading platform.
      </p>

      {/* 1. Information We Collect */}
      <section className={styles.section} id="info-collect">
        <h2 className={styles.sectionTitle}>1. Information We Collect</h2>

        <p>
          <strong>Account Information</strong>
        </p>
        <ul>
          <li>Email address and name (collected during registration via AWS Cognito)</li>
          <li>Authentication data (managed securely by AWS Cognito)</li>
        </ul>

        <p>
          <strong>Brokerage Connection</strong>
        </p>
        <ul>
          <li>
            OAuth tokens from Alpaca Markets (encrypted at rest and stored in AWS DynamoDB). These tokens allow
            SignalDeck to access your brokerage account on your behalf.
          </li>
          <li>We do not store your brokerage username or password.</li>
        </ul>

        <p>
          <strong>Usage Data</strong>
        </p>
        <ul>
          <li>Pages visited and features used within SignalDeck</li>
          <li>Device type, browser type, and general location (country/region)</li>
          <li>Error logs and performance metrics</li>
        </ul>

        <p>
          <strong>Trading Data</strong>
        </p>
        <ul>
          <li>
            Positions, orders, and profit/loss information accessed via the Alpaca API in real time. This data is
            displayed in the app but is not permanently stored by SignalDeck.
          </li>
        </ul>
      </section>

      {/* 2. How We Use Information */}
      <section className={styles.section} id="info-use">
        <h2 className={styles.sectionTitle}>2. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, operate, and improve the SignalDeck platform</li>
          <li>Display your trading data, portfolio analytics, and performance metrics</li>
          <li>Generate AI-powered predictions and market analysis</li>
          <li>Execute automated trading strategies on your behalf (at your direction)</li>
          <li>Send service-related communications (account alerts, security notices)</li>
          <li>Diagnose technical issues and improve platform reliability</li>
        </ul>
      </section>

      {/* 3. Information Sharing */}
      <section className={styles.section} id="info-sharing">
        <h2 className={styles.sectionTitle}>3. Information Sharing</h2>
        <div className={styles.callout}>
          <p>
            <strong>We do NOT sell your personal data.</strong> We never have and never will.
          </p>
        </div>
        <p>We may share information with the following parties:</p>
        <ul>
          <li>
            <strong>Alpaca Markets</strong> — Your brokerage provider, for the purpose of executing trades and accessing
            account data, as authorized by you through OAuth.
          </li>
          <li>
            <strong>Amazon Web Services (AWS)</strong> — Our infrastructure provider. Data is processed and stored on
            AWS services including Cognito (authentication), DynamoDB (data storage), and Lambda (compute).
          </li>
          <li>
            <strong>Law enforcement or regulators</strong> — When required by law, subpoena, or legal process, or to
            protect the rights, property, or safety of SignalDeck and its users.
          </li>
        </ul>
      </section>

      {/* 4. Data Security */}
      <section className={styles.section} id="data-security">
        <h2 className={styles.sectionTitle}>4. Data Security</h2>
        <p>We take the security of your data seriously and employ multiple layers of protection:</p>
        <ul>
          <li>
            <strong>Encryption at rest:</strong> OAuth tokens and sensitive data are encrypted in DynamoDB using AWS Key
            Management Service (KMS).
          </li>
          <li>
            <strong>Encryption in transit:</strong> All communications between your browser, our servers, and
            third-party APIs use HTTPS/TLS.
          </li>
          <li>
            <strong>Authentication:</strong> User sessions are managed through AWS Cognito with JWT-based
            authentication.
          </li>
          <li>
            <strong>Access control:</strong> Per-user data isolation ensures you can only access your own account data.
          </li>
        </ul>
        <p>
          While we implement industry-standard security measures, no system is 100% secure. We encourage you to use a
          strong, unique password and enable multi-factor authentication when available.
        </p>
      </section>

      {/* 5. Data Retention */}
      <section className={styles.section} id="data-retention">
        <h2 className={styles.sectionTitle}>5. Data Retention</h2>
        <p>
          We retain your account data for as long as your account is active. If you delete your account, we will remove
          your personal data and stored OAuth tokens within 30 days, except where retention is required by law.
        </p>
        <p>
          Aggregated, anonymized data that cannot identify you may be retained indefinitely for analytics and service
          improvement purposes.
        </p>
      </section>

      {/* 6. Your Rights */}
      <section className={styles.section} id="your-rights">
        <h2 className={styles.sectionTitle}>6. Your Rights</h2>
        <p>
          Depending on your location, you may have rights under the GDPR, CCPA, or other privacy regulations. These
          include the right to:
        </p>
        <ul>
          <li>
            <strong>Access your data</strong> — Request a copy of the personal information we hold about you.
          </li>
          <li>
            <strong>Delete your account and data</strong> — Request that we delete your account and all associated
            personal data.
          </li>
          <li>
            <strong>Export your data</strong> — Receive your data in a portable, machine-readable format.
          </li>
          <li>
            <strong>Opt out of non-essential communications</strong> — Unsubscribe from marketing or promotional emails
            at any time.
          </li>
          <li>
            <strong>Correct inaccurate data</strong> — Request updates to any incorrect personal information.
          </li>
        </ul>
        <p>
          To exercise any of these rights, contact us at{' '}
          <a href="mailto:privacy@signaldeck.app" className={styles.contactEmail}>
            privacy@signaldeck.app
          </a>
          . We will respond within 30 days.
        </p>
      </section>

      {/* 7. Cookies */}
      <section className={styles.section} id="cookies">
        <h2 className={styles.sectionTitle}>7. Cookies</h2>
        <p>
          SignalDeck uses <strong>essential cookies only</strong>. These are required for authentication and maintaining
          your session. We do not use third-party tracking cookies, advertising cookies, or analytics cookies that track
          you across other websites.
        </p>
      </section>

      {/* 8. Children's Privacy */}
      <section className={styles.section} id="children">
        <h2 className={styles.sectionTitle}>8. Children's Privacy</h2>
        <p>
          SignalDeck is not directed at anyone under the age of 18. We do not knowingly collect personal information
          from minors. If we learn that we have collected data from a user under 18, we will delete that information
          promptly.
        </p>
      </section>

      {/* 9. Changes */}
      <section className={styles.section} id="changes">
        <h2 className={styles.sectionTitle}>9. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. When we make material changes, we will notify you via
          email or in-app notification and update the "Last updated" date at the top of this page. Your continued use of
          SignalDeck after such changes constitutes acceptance of the updated policy.
        </p>
      </section>

      {/* 10. Contact */}
      <section className={styles.section} id="contact">
        <h2 className={styles.sectionTitle}>10. Contact</h2>
        <p>
          If you have questions or concerns about this Privacy Policy or our data practices, please contact us at{' '}
          <a href="mailto:privacy@signaldeck.app" className={styles.contactEmail}>
            privacy@signaldeck.app
          </a>
          .
        </p>
      </section>

      {/* Back to top */}
      <div className={styles.backToTop}>
        <a href="#top" className={styles.backToTopLink}>
          Back to top
        </a>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
